import _ from 'lodash';
import { checkLimits } from 'shared/helpers/check-limits';
import { getLimitPerCustomer, optionValMap, categorySingularize } from 'shared/helpers/products';
import { ERR_OVER_CATEGORY_LIMIT, ERR_OVER_PURCHASE_LIMIT } from 'shared/checkout/actions/validate-cart';

function checkCategoryLimits({ cart, dispensary }) {
  const { categoryLimitsEnabled, categoryLimits } = dispensary;
  const productTypes = _.map(_.keys(cart), (k) => cart[k].product.type);
  const quantities = _.map(_.keys(cart), (k) => cart[k].quantity);
  const options = _.map(_.keys(cart), (k) => cart[k].option);

  if (categoryLimitsEnabled) {
    /* eslint-disable */
    for (const type of _.uniq(productTypes)) {
      const indexes = _.reduce(productTypes, (acc, val, index) => (val === type ? [...acc, index] : acc), []);
      const [{ value: limitForType = false } = {}] = _.filter(categoryLimits, (c) => c.name === type);
      const amnts = _.map(indexes, (i) => {
        if (type === 'Flower') {
          return _.get(optionValMap, options[i], 0.0) * parseInt(quantities[i], 10);
        }
        return parseInt(quantities[i], 10);
      });
      if (limitForType && parseFloat(limitForType) < _.sum(amnts)) {
        const singularType = _.lowerCase(categorySingularize(type));
        let message = `Sorry, we only allow the purchase of ${limitForType}g of ${singularType} per customer.`;
        if (type !== 'Flower') {
          message = `Sorry, we only allow the purchase of ${limitForType} ${singularType} items per customer.`;
        }
        return {
          withinLimits: false,
          targetTypes: type,
          message,
        };
      }
    }
    /* eslint-enable */
  }
  return { withinLimits: true };
}

function checkLimitPerCustomer({ newItemId, cart }) {
  const { product, option, quantity } = cart[newItemId];
  const limitPerCustomer = getLimitPerCustomer(product, option);

  if (_.isFinite(limitPerCustomer) && limitPerCustomer < quantity) {
    return {
      withinLimits: false,
      message: `We're sorry, this item has a limit of ${limitPerCustomer} per customer`,
    };
  }

  return { withinLimits: true };
}

export function checkAllLimits({
  newItemId,
  itemBeingAdded,
  itemBeingRemoved = null,
  existingItems,
  medical,
  dispensary,
  customerState,
  changingQuantity = false,
  complianceOnly = false, // only checks purchasing limits (via checkLimits)
}) {
  let updatedCart = { ...existingItems };
  if (newItemId && itemBeingAdded) {
    const itemsInCart = _.values(existingItems || {});
    const matchingProduct = _.find(itemsInCart, {
      id: itemBeingAdded.product.id,
      option: itemBeingAdded.option,
    });
    let newQuantity = Number(itemBeingAdded.quantity);
    if (matchingProduct && !changingQuantity) {
      newQuantity += Number(matchingProduct.quantity);
    }
    updatedCart = { ...existingItems, [newItemId]: { ...itemBeingAdded, quantity: newQuantity } };
  }

  // if we're simultaneously removing an item (ie. changing weights)
  // make sure we factor that in before we check limits
  if (itemBeingRemoved) {
    delete updatedCart[itemBeingRemoved];
  }

  const withinDispensaryCategoryLimits = checkCategoryLimits({
    cart: updatedCart,
    dispensary,
  });
  if (!withinDispensaryCategoryLimits.withinLimits) {
    return { ...withinDispensaryCategoryLimits, code: ERR_OVER_CATEGORY_LIMIT };
  }

  const withinLegalPurchasingLimits = checkLimits({
    medical,
    state: dispensary.location.state,
    cart: updatedCart,
    purchaseState: customerState,
  });
  if (!dispensary.storeSettings.disablePurchaseLimits && !withinLegalPurchasingLimits.withinLimits) {
    return { ...withinLegalPurchasingLimits, code: ERR_OVER_PURCHASE_LIMIT };
  }

  if (complianceOnly) {
    return { withinLimits: true };
  }

  if (newItemId) {
    const withinDispensaryItemLimits = checkLimitPerCustomer({ newItemId, cart: updatedCart });
    if (!withinDispensaryItemLimits.withinLimits) {
      return withinDispensaryItemLimits;
    }
  }

  return { withinLimits: true };
}
